import { useTranslation } from "react-i18next";
import { Tab } from "semantic-ui-react";

import { ROBSlaTable } from "components/ROB/components";
import { useAgreementColumns, useRateColumns, useRentalClassColumns } from "components/ROB/components/ROBSla/columns";

import type { ROBSlas, SlaSections } from "components/ROB/queryKeys";

type ROBSlaProps = {
  slas: ROBSlas;
};

type SlaSource = {
  sla: string;
  sections: SlaSections;
};

const getRateSources = (slas: ROBSlas): SlaSource[] => [
  ...slas.agreements.map(agreement => ({ sla: agreement.name, sections: agreement })),
  { sla: "", sections: slas.default }
];

const getRepairAndMaintenanceRates = (sources: SlaSource[]) =>
  sources.flatMap(({ sla, sections }) => {
    const { current } = sections.repairAndMaintenance.rates;

    return (current?.values ?? []).map(value => ({ ...value, sla, startDate: current?.startDate ?? "" }));
  });

const getTireRates = (sources: SlaSource[]) =>
  sources.flatMap(({ sla, sections }) => {
    const { rates, preferredMakes } = sections.tires;
    const makeNames = preferredMakes.current?.values.map(({ tireMake }) => `${tireMake.code} ${tireMake.name}`) ?? [];

    return (rates.current?.values ?? []).map(value => ({ ...value, sla, startDate: rates.current?.startDate ?? "", preferredMakes: makeNames.join(", ") }));
  });

const getRentalClasses = (sources: SlaSource[]) =>
  sources.flatMap(({ sla, sections }) => {
    const { current } = sections.rentalVehicle.rates;

    return (current?.rentalClasses ?? []).map(rentalClass => ({ ...rentalClass, sla, startDate: current?.startDate ?? "" }));
  });

export const ROBSla = ({ slas }: ROBSlaProps) => {
  const { t } = useTranslation();

  const rateColumns = useRateColumns();
  const rentalClassColumns = useRentalClassColumns();
  const agreementColumns = useAgreementColumns();

  const sources = getRateSources(slas);
  const repairAndMaintenanceRates = getRepairAndMaintenanceRates(sources);
  const tireRates = getTireRates(sources);
  const rentalClasses = getRentalClasses(sources);

  const panes = [
    {
      menuItem: t("v8_repair_and_maintenance", "Repair & Maintenance"),
      render: () => (
        <Tab.Pane>
          <ROBSlaTable
            key="repairAndMaintenance"
            rows={repairAndMaintenanceRates}
            columns={rateColumns}
            columnVisibility={{ objectTypeCategory: false, preferredMakes: false }}
          />
        </Tab.Pane>
      )
    },
    {
      menuItem: t("v8_tires", "Tires"),
      render: () => (
        <Tab.Pane>
          <ROBSlaTable key="tires" rows={tireRates} columns={rateColumns} />
        </Tab.Pane>
      )
    },
    {
      menuItem: t("v8_rental_vehicle", "Rental vehicle"),
      render: () => (
        <Tab.Pane>
          <ROBSlaTable key="rentalVehicle" rows={rentalClasses} columns={rentalClassColumns} />
        </Tab.Pane>
      )
    },
    ...(slas.agreements.length
      ? [
          {
            menuItem: t("v8_agreements", "Agreements"),
            render: () => (
              <Tab.Pane>
                <ROBSlaTable key="agreements" rows={slas.agreements} columns={agreementColumns} />
              </Tab.Pane>
            )
          }
        ]
      : [])
  ];

  return <Tab menu={{ secondary: true }} panes={panes} />;
};
